/**
 * AuthLogo Component
 * Static logo for authentication pages (no navigation link)
 */

import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { APP_AUTH_HEADING, APP_LOGO_ALT, APP_NAME } from '@/constants/brand';

interface AuthLogoProps {
  /** Height of the logo in pixels */
  height?: number;
  /** Whether to show the text label below the logo */
  showLabel?: boolean;
}

/**
 * AuthLogo - Static branding logo for auth pages
 *
 * Unlike the AppBar Logo, this component does not link anywhere.
 * It displays the Mensova logo with optional label.
 *
 * @example
 * ```tsx
 * <AuthLogo height={48} showLabel />
 * ```
 */
export const AuthLogo: React.FC<AuthLogoProps> = ({ height = 48, showLabel = true }) => {
  const [hasError, setHasError] = useState(false);
  const logoSrc = '/images/logo.svg';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
      }}
    >
      {/* Logo Image with Fallback */}
      {!hasError ? (
        <Box
          component="img"
          src={logoSrc}
          alt={APP_LOGO_ALT}
          sx={{
            height,
            maxWidth: '100%',
            objectFit: 'contain',
          }}
          onError={() => setHasError(true)}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            width: height,
            height: height,
            borderRadius: 1,
            bgcolor: 'primary.main',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: height * 0.4,
            color: 'primary.contrastText',
          }}
          aria-label={APP_NAME}
        >
          M
        </Box>
      )}

      {/* Optional Label */}
      {showLabel && (
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            textAlign: 'center',
          }}
        >
          {APP_AUTH_HEADING}
        </Typography>
      )}
    </Box>
  );
};
